/**
 * components/settings/SettingsProfileSection.tsx
 *
 * Section Profil des paramètres.
 * Affiche l'avatar (initiales), le résumé d'identité et un formulaire éditable
 * pour le prénom et le nom. L'email est affiché en lecture seule avec icône Lock.
 * La sauvegarde est déclenchée via la SettingsSaveBar dans la page parente.
 * Updated: suppression du titre h2 (déplacé dans SettingsSection), suppression
 * de "Membre depuis", séparateur visuel entre identité et formulaire,
 * champ email renforcé visuellement (iconRight Lock + disabled styling).
 * Updated: helper text email raccourci + lien mailto cliquable pour le support.
 * Updated: ajout de la section logo sous le champ email - logo de profil automatiquement
 * appliqué aux nouveaux formulaires, avec option de retrait ou remplacement.
 * Updated: ajout de la section tampon sous le logo - SettingsStampSection avec aperçu SVG
 * en temps réel généré depuis l'intitulé et le numéro de toque.
 * Updated: replaced <label> with <h3> for the "Logo" section title for correct semantics.
 * Updated: ajout de stampRef pour le scroll automatique vers #stamp depuis la modale dossier.
 */

import { type RefObject } from 'react';
import { Lock } from 'lucide-react';
import Input from '../ui/Input';
import Spinner from '../ui/Spinner';
import LogoUploader from '../ui/LogoUploader';
import SettingsStampSection from './SettingsStampSection';
import { ProfileData } from '../../hooks/useSettingsProfile';

interface SettingsProfileSectionProps {
  profile: ProfileData | null;
  loading: boolean;
  onUpdateField: (field: keyof Omit<ProfileData, 'email' | 'created_at'>, value: string) => void;
  logoFile: File | null;
  logoPreviewUrl: string | null;
  onLogoChange: (file: File | null, url: string | null) => void;
  stampRef?: RefObject<HTMLDivElement>;
}

function getColorFromEmail(email: string): string {
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-pink-500',
    'bg-orange-500',
    'bg-teal-500',
    'bg-cyan-500',
    'bg-emerald-500',
    'bg-rose-500',
  ];
  const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
}

function getInitials(profile: ProfileData): string {
  const firstName = profile.first_name.trim();
  const lastName = profile.last_name.trim();
  if (firstName && lastName) return (firstName[0] + lastName[0]).toUpperCase();
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  return profile.email.slice(0, 2).toUpperCase();
}

function getDisplayName(profile: ProfileData): string {
  const firstName = profile.first_name.trim();
  const lastName = profile.last_name.trim();
  if (firstName || lastName) return [firstName, lastName].filter(Boolean).join(' ');
  return profile.email.split('@')[0];
}

export default function SettingsProfileSection({
  profile,
  loading,
  onUpdateField,
  logoFile,
  logoPreviewUrl,
  onLogoChange,
  stampRef,
}: SettingsProfileSectionProps) {
  if (loading) {
    return (
      <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-soft flex justify-center py-12">
        <div className="text-primary-600">
          <Spinner size="md" />
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const avatarColor = getColorFromEmail(profile.email);
  const initials = getInitials(profile);
  const displayName = getDisplayName(profile);

  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-soft">
      <div className="flex items-center gap-4 pb-6">
        <div
          className={`w-14 h-14 rounded-full ${avatarColor} flex items-center justify-center flex-shrink-0`}
        >
          <span className="text-white font-semibold text-lg">{initials}</span>
        </div>
        <div className="min-w-0">
          <p className="text-base font-semibold text-neutral-900 truncate">{displayName}</p>
          <p className="text-sm text-neutral-500 truncate">{profile.email}</p>
        </div>
      </div>

      <div className="border-t border-neutral-100 pt-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Prénom"
            value={profile.first_name}
            onChange={(e) => onUpdateField('first_name', e.target.value)}
            autoComplete="given-name"
          />
          <Input
            label="Nom"
            value={profile.last_name}
            onChange={(e) => onUpdateField('last_name', e.target.value)}
            autoComplete="family-name"
          />
          <div className="sm:col-span-2">
            <Input
              label="Email"
              value={profile.email}
              disabled
              iconRight={<Lock className="w-4 h-4 text-neutral-400" />}
            />
            <p className="mt-1.5 text-xs text-neutral-500">
              Adresse email non modifiable.{' '}
              <a
                href="mailto:support@qollect.app"
                className="text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors"
              >
                Contacter le support
              </a>
            </p>
          </div>
        </div>

        <div id="profile-logo" className="border-t border-neutral-100 pt-4">
          <h3 className="block text-sm font-medium text-neutral-700 mb-2">
            Logo
          </h3>
          <LogoUploader
            value={logoFile}
            previewUrl={logoPreviewUrl}
            onChange={onLogoChange}
            variant="card"
          />
          <p className="mt-1.5 text-xs text-neutral-500">
            Ce logo apparaîtra dans vos formulaires.
          </p>
        </div>

        <SettingsStampSection
          profile={profile}
          onUpdateField={onUpdateField}
          sectionRef={stampRef}
        />
      </div>
    </div>
  );
}
